import React, { createContext, useEffect, useCallback, useState } from "react";
const ChromeExtensionContext = createContext();
const useChromeExtension = () => {
  const context = React.useContext(ChromeExtensionContext);
  if (context === undefined) {
    throw new Error(
      "`useChromeExtension` hook must be used within a `ChromeExtensionContextProvider` component"
    );
  }
  return context;
};

export const EXTENSION_VERSION_UNKNOWN = "__unknown__";
export const EXTENSION_NOT_INSTALLED = "__not_installed__";
export const EXTENSION_INSTALLED = "__installed__";
export const EXTENSION_MIN_VERSION = [2, 0, 0]; // [major, minor, revision]

const ChromeExtensionContextProvider = ({ children }) => {
  const [extensionStatus, setExtensionStatus] = useState(
    EXTENSION_VERSION_UNKNOWN
  );
  const [extensionVersions, setExtensionVersions] = useState([]);

  useEffect(() => {
    const requestVersion = () => {
      const request = {
        what: "CHROME_EXTENSION_VERSION_REQUEST",
      };
      window.postMessage(request, "*");
    };
    // Request version after the page load
    // The delay is needed to wait until the content script is started
    setTimeout(requestVersion, 300);
    // If extensition didn't report its version within 400 msecs, consider that it's not installed
    setTimeout(() => {
      setExtensionStatus((oldExtensionStatus) =>
        oldExtensionStatus === EXTENSION_VERSION_UNKNOWN
          ? EXTENSION_NOT_INSTALLED
          : oldExtensionStatus
      );
    }, 400);
  }, []);
  const onMessageHandler = useCallback((event) => {
    const { data } = event;
    const { payload, what } = data;
    if (what === "CHROME_EXTENSION_VERSION") {
      const { version } = payload;

      setExtensionStatus(EXTENSION_INSTALLED);
      setExtensionVersions((prev) =>
        prev.includes(version) ? prev : [...prev, version]
      );
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", onMessageHandler);
    return () => {
      window.removeEventListener("message", onMessageHandler);
    };
  }, [onMessageHandler]);

  const isExtensionInstalled = () => {
    if (extensionStatus !== EXTENSION_NOT_INSTALLED) {
      return true;
    } else {
      return false;
    }
  };

  const isExtensionUpToDate = () => {
    if (
      extensionStatus === EXTENSION_VERSION_UNKNOWN ||
      extensionStatus === EXTENSION_NOT_INSTALLED ||
      extensionVersions.length > 1
    ) {
      return false;
    }
    const parts = extensionVersions[0].split(".");
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] < EXTENSION_MIN_VERSION[i]) {
        return false;
      } else if (parts[i] > EXTENSION_MIN_VERSION[i]) {
        return true;
      }
    }
    return true;
  };

  const hasOldAndNewExtensionTogether = () => {
    if (extensionVersions.length === 1) return false;

    // Check for at least one version starting with '1.' and one with '2.'
    const hasV1 = extensionVersions.some((v) => v.startsWith("1."));
    const hasV2 = extensionVersions.some((v) => v.startsWith("2."));
    if (hasV1 && hasV2) {
      return true;
    }
    return false;
  };

  const extensionVersion =
    extensionStatus === EXTENSION_NOT_INSTALLED
      ? EXTENSION_NOT_INSTALLED
      : extensionStatus === EXTENSION_VERSION_UNKNOWN
      ? EXTENSION_VERSION_UNKNOWN
      : extensionVersions[0] || EXTENSION_VERSION_UNKNOWN;

  return (
    <ChromeExtensionContext.Provider
      value={{
        extensionStatus,
        extensionVersion,
        extensionVersions,
        isExtensionInstalled,
        isExtensionUpToDate,
        hasOldAndNewExtensionTogether,
      }}
    >
      {children}
    </ChromeExtensionContext.Provider>
  );
};
export { ChromeExtensionContextProvider, useChromeExtension };
